import React, { memo, useMemo, useRef, useState } from 'react';
import {
  Center,
  Flex,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
} from '@chakra-ui/react';
import { useQuery, useQueryClient } from 'react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useSites, useWashHubListener } from 'hooks';
import AddInventoryCountModal, {
  RefType,
} from 'components/modules/AddInventoryCountModal';
import EditInventoryCountModal, {
  EditRefType,
} from 'components/modules/EditInventoryCountModal';
import { AddHarvestSummaryModal } from 'components/modules/AddHarvestSummaryModal';
import { DataTable, ServerSort } from 'components/modules/DataTable';
import {
  archiveInventoryCount,
  deleteInventoryCount,
  fetchAllCounts,
  manualRefreshTriggerById,
} from 'api/inventory';
import { washHub } from 'store';
import { InventoryCount } from 'types/inventory';
import { InventoryCountTabProps } from './InventoryCount.props';
import Alert, { AlertRefType } from 'components/primitives/Alert';
import RefreshSvg from 'svg/Refresh';
import ArchiveSvg from 'svg/Archive';
import DecomissionedSvg from 'svg/Decommissioned';
import { FaEllipsisV } from 'react-icons/fa';
import { AddHarvestSummaryModalRefType } from 'components/modules/AddHarvestSummaryModal/AddHarvestSummaryModal.props';
import AddSvg from 'svg/AddSvg';
import { buildInventoryCountColumns } from './inventoryCountColumns';

const actionsCol = createColumnHelper<InventoryCount>();

export const InventoryTabContent = memo<InventoryCountTabProps>((props) => {
  const {
    endDate,
    startDate,
    site,
    accountManagers,
    itemCount,
    caseCount,
    status,
  } = props;
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const [page, setPage] = useState<number>(1);
  const [sort, setSort] = useState<ServerSort | undefined>(undefined);
  const { data: sites } = useSites(true);
  const washHubId = washHub.selectors.useSelectedWashHubId();
  const [countToArchive, setCountToArchive] = useState('');
  const addInventoryCountModalRef = useRef<RefType>(null);
  const editInventoryCountModalRef = useRef<EditRefType>(null);
  const queryClient = useQueryClient();
  const alertRef = useRef<AlertRefType>(null);
  const toast = useToast();
  const addHarvestSummaryModalRef = useRef<AddHarvestSummaryModalRefType>(null);

  const queryKey = [
    'fetch-inventory-counts',
    page,
    status ?? undefined,
    washHubId,
    endDate,
    startDate,
    site?.toString(),
    accountManagers?.toString(),
    sort?.field,
    sort?.dir,
  ];
  const queryFn = () =>
    fetchAllCounts({
      ...(status !== undefined ? { status } : {}),
      page,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
      site,
      ...(accountManagers && accountManagers.length ? { accountManagers } : {}),
      sortByField: sort?.field,
      sort: sort?.dir,
    });
  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
  };
  const { data, isFetching } = useQuery(queryKey, queryFn, options);
  const counts = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = { page, setPage, totalCount, totalPages };

  const invalidateQuery = () => {
    queryClient.invalidateQueries('fetch-inventory-counts');
  };

  useWashHubListener(() => {
    setPage(1);
  });

  const columns = useMemo(
    () => [
      ...buildInventoryCountColumns(sites, itemCount, caseCount),
      actionsCol.display({
        id: 'actions',
        header: '',
        minSize: 130,
        size: 130,
        meta: { truncate: false },
        cell: ({ row: { original: count } }) => (
          <Flex
            justifyContent="center"
            alignItems="center"
            onClick={(e) => e.stopPropagation()}>
            <div
              hidden={count?.status !== 3 && count?.status !== 4}
              onClick={() => {
                addHarvestSummaryModalRef.current?.onOpen(count);
              }}
              title="Click to Create Report">
              <AddSvg />
            </div>
            <div
              style={{
                width:
                  count?.status !== 3 && count?.status !== 4 ? '32px' : '8px',
              }}
            />
            <div
              onClick={async () => {
                await manualRefreshTriggerById(count?._id);
                toast({
                  status: 'success',
                  title: 'Success',
                  description: 'Inventory Count Refreshed',
                  isClosable: true,
                  duration: 5000,
                });
              }}
              title="Click to refresh">
              <RefreshSvg />
            </div>
            <div style={{ width: '8px' }} />
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<FaEllipsisV />}
                variant="ghost"
                aria-label="Options"
              />
              <MenuList>
                <MenuItem
                  icon={<ArchiveSvg />}
                  onClick={async () => {
                    setCountToArchive(count._id);
                    if (count.harvestCount > 0) {
                      alertRef.current?.onOpen?.();
                    } else {
                      await archiveInventoryCount(count?._id);
                      toast({
                        status: 'success',
                        title: 'Success',
                        description: 'Inventory Count Archived',
                        isClosable: true,
                        duration: 5000,
                      });
                      invalidateQuery();
                    }
                  }}>
                  Archive
                </MenuItem>
                <MenuItem
                  icon={<DecomissionedSvg />}
                  onClick={async () => {
                    await deleteInventoryCount(count._id);
                    toast({
                      status: 'success',
                      title: 'Success',
                      description: 'Inventory Count Deleted',
                      isClosable: true,
                      duration: 5000,
                    });
                    invalidateQuery();
                  }}>
                  Delete
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sites, itemCount, caseCount],
  );

  return (
    <Flex flex={1} direction="column" color="support.500" overflow="auto">
      <Alert
        ref={alertRef}
        title="Archive Inventory Count"
        body={`By marking this Inventory Count as Archived, you are also archiving Harvest Summaries, do you want to proceed?`}
        button1="Cancel"
        button2="Proceed"
        onCancelClick={() => {}}
        onSaveClick={async () => {
          await archiveInventoryCount(countToArchive);
          toast({
            status: 'success',
            title: 'Success',
            description: 'Harvest Summary Archived',
            isClosable: true,
            duration: 5000,
          });
          invalidateQuery();
        }}
        closeOnOverlayClick={false}
      />

      <DataTable<InventoryCount>
        minW="1096px"
        data={counts}
        columns={columns}
        isLoading={isFetching}
        pagination={pagination}
        sort={sort}
        onSortChange={setSort}
        onRowClick={(count) => {
          editInventoryCountModalRef.current?.onOpen(count);
        }}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Inventory counts available</Text>
          </Center>
        )}
      />

      <AddInventoryCountModal ref={addInventoryCountModalRef} />
      <EditInventoryCountModal ref={editInventoryCountModalRef} />
      <AddHarvestSummaryModal ref={addHarvestSummaryModalRef} />
    </Flex>
  );
});
